import { getKeycloakInstance } from "@auth/keycloakClient";
import { isLegacyAuth } from "@configs/authMode";
import ToastConstant from "@configs/react-toastify/snackbar";
import { ERROR_CODE, LOCAL_STORAGE, TOAST, ERROR_API } from "@constants/index";
import { logoutOnSessionExpired } from "@utils/authSession";
import axios from "axios";
import i18n from "i18next";

const getRuntimeConfig = () => {
  if (typeof window !== "undefined" && window.__APP_CONFIG__) {
    return window.__APP_CONFIG__;
  }
  return {
    API_BASE_URL: import.meta.env.VITE_API_BASE_URL || "/api/v1",
    PY_API_BASE_URL: import.meta.env.VITE_PYTHON_API_BASE_URL || "/py",
    BASE_PATH: import.meta.env.VITE_BASE_PATH || "/",
    EXTERNAL_HTTP_PORT: import.meta.env.VITE_EXTERNAL_HTTP_PORT || "80",
    EXTERNAL_HTTPS_PORT: import.meta.env.VITE_EXTERNAL_HTTPS_PORT || "443",
  };
};

const config = getRuntimeConfig();
axios.defaults.baseURL = config.API_BASE_URL;

let activeRequests = 0;
const incrementActiveRequests = () => {
  activeRequests += 1;
};
const decrementActiveRequests = () => {
  activeRequests = Math.max(0, activeRequests - 1);
};
export const getActiveRequestCount = () => activeRequests;
export const isApiBusy = () => activeRequests > 0;

let isRedirecting = false;
let hasShownAuthErrorToast = false;
/** Same-tab mutex when Web Locks API is unavailable. */
let isRefreshing = false;
let failedQueue = [];

const AUTH_REFRESH_LOCK_NAME = "csc-auth-refresh";

const applyAccessTokenAndRetry = (originalRequest, accessToken) => {
  originalRequest.headers = originalRequest.headers || {};
  originalRequest.headers.Authorization = `Bearer ${accessToken}`;
  return API(originalRequest);
};

const flushQueueWithToken = (accessToken) => {
  failedQueue.forEach(({ resolve, config: queuedConfig }) => {
    queuedConfig.headers = queuedConfig.headers || {};
    queuedConfig.headers.Authorization = `Bearer ${accessToken}`;
    resolve(API(queuedConfig));
  });
  failedQueue = [];
};

const rejectQueue = (error) => {
  failedQueue.forEach(({ reject }) => {
    reject(error);
  });
  failedQueue = [];
};

const handleTokenExpiration = async (options = {}) => {
  if (isRedirecting) {
    return false;
  }
  isRedirecting = true;
  let redirectStarted = false;
  try {
    redirectStarted = await logoutOnSessionExpired(options);
    return redirectStarted;
  } catch (error) {
    console.error("Failed to handle session expiration:", error);
    return false;
  } finally {
    if (!redirectStarted) {
      isRedirecting = false;
      hasShownAuthErrorToast = false;
    }
  }
};

const handleKeycloakRefreshToken = async (originalRequest) => {
  const kc = getKeycloakInstance();
  if (!kc?.authenticated) {
    await handleTokenExpiration();
    return Promise.reject(new Error("Keycloak session ended"));
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject, config: originalRequest });
    });
  }

  isRefreshing = true;
  try {
    await kc.updateToken(30);
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = `Bearer ${kc.token}`;
    flushQueueWithToken(kc.token);
    isRefreshing = false;
    return API(originalRequest);
  } catch (error) {
    rejectQueue(error);
    isRefreshing = false;
    await handleTokenExpiration();
    return Promise.reject(error);
  }
};

/** True when storage moved past the session/token this tab started with. */
const hasAdoptedCredentials = (sessionIdUsed, tokenBefore) => {
  const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const sessionId = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
  return Boolean(
    token &&
      sessionId &&
      sessionId !== sessionIdUsed &&
      (!tokenBefore || token !== tokenBefore),
  );
};

/** Server confirmed the refresh session is dead (not network/5xx). */
const isConfirmedRefreshRejection = (error) => {
  const status = error?.response?.status;
  return status === ERROR_CODE.UNAUTHORIZED || status === ERROR_CODE.BAD_REQUEST;
};

/**
 * Legacy refresh under Web Locks (or same-tab mutex).
 * Keeps existing TOKEN / SESSION_ID keys — no AUTH_STATE.
 *
 * - Winner writes SESSION_ID then TOKEN
 * - Loser adopts if storage already has a new pair
 * - Confirmed invalid session → scoped logout; transient errors leave the server session alone
 */
const runLegacyRefresh = async (originalRequest, sessionIdUsed, tokenBefore) => {
  // Another tab may have finished while we waited for the lock.
  if (hasAdoptedCredentials(sessionIdUsed, tokenBefore)) {
    const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
    flushQueueWithToken(token);
    return applyAccessTokenAndRetry(originalRequest, token);
  }

  if (!sessionIdUsed) {
    await handleTokenExpiration({});
    throw new Error("Legacy session ended");
  }

  try {
    const res = await axios.post(
      "/refresh_token",
      { sessionId: sessionIdUsed },
      {
        skipErrorToast: true,
        skipAuthRefresh: true,
      },
    );
    const accessToken = res.data?.accessToken;
    const newSessionId = res.data?.sessionId;
    if (!accessToken || !newSessionId) {
      throw new Error("Refresh response missing accessToken or sessionId");
    }

    localStorage.setItem(LOCAL_STORAGE.SESSION_ID, newSessionId);
    localStorage.setItem(LOCAL_STORAGE.TOKEN, accessToken);
    flushQueueWithToken(accessToken);
    return applyAccessTokenAndRetry(originalRequest, accessToken);
  } catch (error) {
    if (hasAdoptedCredentials(sessionIdUsed, tokenBefore)) {
      const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
      flushQueueWithToken(token);
      return applyAccessTokenAndRetry(originalRequest, token);
    }

    const currentSessionId = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
    if (
      isConfirmedRefreshRejection(error) &&
      (!currentSessionId || currentSessionId === sessionIdUsed)
    ) {
      await handleTokenExpiration({ sessionIdToRevoke: sessionIdUsed });
    }

    rejectQueue(error);
    throw error;
  }
};

const handleLegacyRefreshToken = async (originalRequest) => {
  const sessionIdUsed =
    originalRequest._legacySessionIdAtRefresh ||
    localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
  const tokenBefore =
    originalRequest._legacyTokenAtRefresh ||
    localStorage.getItem(LOCAL_STORAGE.TOKEN);

  if (!sessionIdUsed) {
    await handleTokenExpiration({});
    return Promise.reject(new Error("Legacy session ended"));
  }

  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(AUTH_REFRESH_LOCK_NAME, () =>
      runLegacyRefresh(originalRequest, sessionIdUsed, tokenBefore),
    );
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject, config: originalRequest });
    });
  }
  isRefreshing = true;
  try {
    return await runLegacyRefresh(originalRequest, sessionIdUsed, tokenBefore);
  } finally {
    isRefreshing = false;
  }
};

const handleRefreshToken = (originalRequest) => {
  if (isLegacyAuth()) {
    return handleLegacyRefreshToken(originalRequest);
  }
  return handleKeycloakRefreshToken(originalRequest);
};

export const API = axios.create({
  baseURL: config.API_BASE_URL,
});

const PUBLIC_AUTH_PATHS = ["/login", "/refresh_token", "/logout", "/locale_default"];

const normalizeRequestPath = (url = "") => url.split("?")[0].replace(/\/+$/, "") || "/";

const isPublicAuthPath = (path) =>
  PUBLIC_AUTH_PATHS.some((publicPath) => path === publicPath || path.endsWith(publicPath));

const shouldSkipAuth = (requestConfig) => {
  if (requestConfig?.skipAuth) {
    return true;
  }
  return isPublicAuthPath(normalizeRequestPath(requestConfig?.url));
};

const isInvalidAccessToken = (response) => {
  const header = response?.headers?.["www-authenticate"] || "";
  return header.includes("invalid_token");
};

const getBearerFromConfig = (requestConfig) => {
  const header = requestConfig?.headers?.Authorization || requestConfig?.headers?.authorization || "";
  if (typeof header === "string" && header.startsWith("Bearer ")) {
    return header.slice(7);
  }
  return null;
};

/** Retry once when localStorage already has a newer access token than the failed request. */
const retryWithNewerStorageTokenIfAny = (requestConfig) => {
  if (!isLegacyAuth() || !requestConfig || requestConfig._retriedWithStorageToken) {
    return null;
  }
  const failedBearer = getBearerFromConfig(requestConfig);
  const currentToken = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const currentSessionId = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
  const sessionAtFail = requestConfig._legacySessionIdAtRefresh;
  if (!currentToken || !failedBearer || currentToken === failedBearer) {
    return null;
  }
  // Prefer a full rotated pair when we know the pre-refresh session id.
  if (sessionAtFail && currentSessionId === sessionAtFail) {
    return null;
  }
  requestConfig._retriedWithStorageToken = true;
  requestConfig.headers = requestConfig.headers || {};
  requestConfig.headers.Authorization = `Bearer ${currentToken}`;
  return API(requestConfig);
};

const handleAPIError = async (error) => {
  const { response, config: requestConfig } = error;

  if (requestConfig?.skipErrorToast) {
    return Promise.reject(error);
  }

  if (response) {
    const errorMessage = response.data?.message || "";
    switch (response.status) {
      case ERROR_CODE.UNAUTHORIZED: {
        if (isLegacyAuth() && requestConfig) {
          requestConfig._legacySessionIdAtRefresh = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
          requestConfig._legacyTokenAtRefresh = localStorage.getItem(LOCAL_STORAGE.TOKEN);
        }

        const storageRetry = retryWithNewerStorageTokenIfAny(requestConfig);
        if (storageRetry) {
          return storageRetry;
        }

        if (isInvalidAccessToken(response)) {
          await handleTokenExpiration({
            sessionIdToRevoke:
              requestConfig?._legacySessionIdAtRefresh ||
              localStorage.getItem(LOCAL_STORAGE.SESSION_ID),
          });
          if (!hasShownAuthErrorToast) {
            ToastConstant(
              TOAST.error,
              i18n.t("toast.api.authentication-error"),
              errorMessage || i18n.t(ERROR_API.unauthorized),
            );
            hasShownAuthErrorToast = true;
          }
          return Promise.reject(error);
        }

        if (requestConfig?.skipAuthRefresh || shouldSkipAuth(requestConfig)) {
          return Promise.reject(error);
        }

        try {
          return await handleRefreshToken(requestConfig);
        } catch (refreshError) {
          const afterRefreshRetry = retryWithNewerStorageTokenIfAny(requestConfig);
          if (afterRefreshRetry) {
            return afterRefreshRetry;
          }
          // Legacy path already adopted or logged out inside the lock.
          if (isLegacyAuth()) {
            return Promise.reject(refreshError);
          }
          await handleTokenExpiration();
          if (!hasShownAuthErrorToast) {
            ToastConstant(
              TOAST.error,
              i18n.t("toast.api.authentication-error"),
              errorMessage || i18n.t(ERROR_API.unauthorized),
            );
            hasShownAuthErrorToast = true;
          }
          return Promise.reject(refreshError);
        }
      }
      case ERROR_CODE.FORBIDDEN:
        ToastConstant(TOAST.error, i18n.t("toast.api.access-denied"), errorMessage || i18n.t(ERROR_API.forbidden));
        break;
      case ERROR_CODE.BAD_REQUEST:
        break;
      case ERROR_CODE.METHOD_NOT_ALLOWED:
        ToastConstant(TOAST.error, i18n.t("toast.api.method-not-allowed"), errorMessage || i18n.t(ERROR_API.methodNotAllow));
        break;
      case ERROR_CODE.GATEWAY_TIMEOUT:
        ToastConstant(TOAST.error, i18n.t("toast.api.request-timeout"), errorMessage || i18n.t(ERROR_API.ERROR_TIMEOUT));
        break;
      case ERROR_CODE.INTERNAL_SERVER_ERROR:
        if (requestConfig?.url?.includes("check-updates")) {
          ToastConstant(
            TOAST.error,
            i18n.t("toast.api.host-service-unreachable"),
            errorMessage || i18n.t(ERROR_API.hostServiceUnreachable),
          );
        }
        break;
      default:
        break;
    }
  }
  return Promise.reject(error);
};

API.interceptors.request.use(
  async (requestConfig) => {
    incrementActiveRequests();
    requestConfig.headers = requestConfig.headers || {};

    if (shouldSkipAuth(requestConfig)) {
      delete requestConfig.headers.Authorization;
      return requestConfig;
    }

    if (isLegacyAuth()) {
      const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
      if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
      return requestConfig;
    }

    const kc = getKeycloakInstance();
    if (kc?.authenticated) {
      try {
        await kc.updateToken(70);
      } catch {
        /* 401 flow may refresh */
      }
      if (kc.token) {
        requestConfig.headers.Authorization = `Bearer ${kc.token}`;
      }
    }
    return requestConfig;
  },
  (error) => {
    decrementActiveRequests();
    return Promise.reject(error);
  },
);

API.interceptors.response.use(
  (response) => {
    decrementActiveRequests();
    return response;
  },
  (error) => {
    decrementActiveRequests();
    return handleAPIError(error);
  },
);

axios.interceptors.request.use(
  (requestConfig) => {
    incrementActiveRequests();
    return requestConfig;
  },
  (error) => {
    decrementActiveRequests();
    return Promise.reject(error);
  },
);
axios.interceptors.response.use(
  (response) => {
    decrementActiveRequests();
    return response;
  },
  (error) => {
    decrementActiveRequests();
    return handleAPIError(error);
  },
);

export function apiBase(options) {
  return new Promise((resolve, reject) => {
    API(options)
      .then((response) => {
        resolve(response.data);
      })
      .catch((errors) => {
        reject(errors);
      });
  });
}
